import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useComfort } from "../app/ComfortContext";
import { getDictionary, LOCALE_LABEL, type Locale } from "../i18n/dictionary";
import { portalMode, adminPath } from "../lib/host";
import "./portal.css";
import { useOrgProfile, OrgStatusBanner } from "../app/admin/orgProfile";
import { useDemoVariant, useLoad } from "../app/admin/ui";
import { getPortalData, shouldUseSupabase } from "../data";
import { setDemoVariant, type DemoVariant } from "../data/demoVariant";

/** Whether the signed-in adult may open the Zooylo owner console. */
function usePlatformAdmin(): boolean {
  const { data } = useLoad(() => getPortalData().amIPlatformAdmin().catch(() => false), []);
  return data === true;
}

type AdminAuthView = "signin" | "sent" | "expired" | "notOrg" | "app";

// ---- Shared SVGs (admin-specific ones not already in ServicePortal) --------

function IconOverview() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  );
}
function IconMembers() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3" /><path d="M3 19a6 6 0 0112 0" /><path d="M16 6a3 3 0 010 6" /><path d="M22 19a6 6 0 00-4-5.7" />
    </svg>
  );
}
function IconSeats() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 11V8a2 2 0 012-2h10a2 2 0 012 2v3" />
      <path d="M4 11h16a1 1 0 011 1v4H3v-4a1 1 0 011-1z" /><path d="M6 16v3M18 16v3" />
    </svg>
  );
}
function IconSponsored() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20s-6.5-4-6.5-8.5A3.5 3.5 0 0112 9.5a3.5 3.5 0 016.5 2c0 4.5-6.5 8.5-6.5 8.5z" />
      <path d="M4 6.5h4M6 4.5v4" />
    </svg>
  );
}
function IconRoster() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 6h13M8 12h13M8 18h13" /><circle cx="3.5" cy="6" r="1.2" /><circle cx="3.5" cy="12" r="1.2" /><circle cx="3.5" cy="18" r="1.2" />
    </svg>
  );
}
function IconConsentShield() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" /><path d="M9 12l2 2 4-4" />
    </svg>
  );
}
function IconData() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" /><path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </svg>
  );
}
function IconSettings() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19 12a7 7 0 00-.1-1l2-1.5-2-3.4-2.3 1a7 7 0 00-1.7-1L16.5 3h-4l-.4 2.6a7 7 0 00-1.7 1l-2.3-1-2 3.4L6 10a7 7 0 000 2l-2 1.5 2 3.4 2.3-1a7 7 0 001.7 1l.4 2.6h4l.4-2.6a7 7 0 001.7-1l2.3 1 2-3.4-2-1.5a7 7 0 00.1-1z" />
    </svg>
  );
}
function IconSignOut() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><path d="M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}
function IconMenu() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}
function IconClose() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
function IconCalm() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12c3-4 6-4 9 0s6 4 9 0" />
    </svg>
  );
}
function IconText() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7V5h12v2M10 5v14M7 19h6M17 13v-1h5v1M19.5 12v7M18 19h3" />
    </svg>
  );
}
function IconSpacing() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}
function IconContrast() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 3a9 9 0 010 18z" fill="currentColor" />
    </svg>
  );
}
function IconMoon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
    </svg>
  );
}
function IconGlobe() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
    </svg>
  );
}
function IconWarning() {
  return (
    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="var(--peach-deep)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5M12 16h.01" />
    </svg>
  );
}
function IconMail() {
  return (
    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="var(--brand-hover)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 7l9 6 9-6" />
    </svg>
  );
}
function IconPersonQuestion() {
  return (
    <svg viewBox="0 0 24 24" width="38" height="38" fill="none" stroke="var(--peach-deep)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" /><path d="M4 20a6 6 0 0110 0" /><path d="M17 9v4M17 16h.01" />
    </svg>
  );
}
function ZooyloLogo({ size = 34 }: { size?: number }) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
      <rect x="2" y="2" width="44" height="44" rx="14" fill="var(--brand)" />
      <circle cx="18" cy="20" r="4" fill="#FBF7F0" />
      <circle cx="30" cy="20" r="4" fill="#FBF7F0" />
      <path d="M16 30 Q24 37 32 30" stroke="#FBF7F0" strokeWidth="3" fill="none" strokeLinecap="round" />
    </svg>
  );
}

// ---- Comfort cluster + panel ----------------------------------------------

interface ComfortClusterProps {
  dict: ReturnType<typeof getDictionary>;
  locale: Locale;
  calm: boolean;
  bigText: boolean;
  spacing: boolean;
  contrast: boolean;
  dark: boolean;
  toggle: (key: "calm" | "bigText" | "spacing" | "contrast" | "dark") => void;
  setLocale: (l: Locale) => void;
  onOpenPanel: () => void;
}

function ComfortCluster({ dict, locale, calm, bigText, spacing, contrast, dark, toggle, setLocale, onOpenPanel }: ComfortClusterProps) {
  const tc = dict.comfort;
  const nextLocale: Locale = locale === "en" ? "ms" : "en";
  const btn: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 6, height: 42, padding: "0 14px",
    border: "1px solid var(--line-strong)", background: "var(--surface)", color: "var(--ink-soft)",
    borderRadius: "var(--r-pill)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13.5,
    cursor: "pointer", whiteSpace: "nowrap" as const,
  };
  return (
    <>
      <div className="zcomfort-cluster" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button className="zcomfort" data-on={String(calm)} onClick={() => toggle("calm")} title={tc.calmMode} style={btn}><IconCalm /> {tc.calm}</button>
        <button className="zcomfort" data-on={String(bigText)} onClick={() => toggle("bigText")} title={tc.biggerText} style={btn}><IconText /> {tc.text}</button>
        <button className="zcomfort" data-on={String(spacing)} onClick={() => toggle("spacing")} title={tc.readingSpacing} style={btn}><IconSpacing /> {tc.spacing}</button>
        <button className="zcomfort" data-on={String(contrast)} onClick={() => toggle("contrast")} title={tc.higherContrast} style={btn}><IconContrast /> {tc.contrast}</button>
        <button className="zcomfort" data-on={String(dark)} onClick={() => toggle("dark")} title={tc.darkTheme} style={btn}><IconMoon /> {dark ? tc.light : tc.dark}</button>
        <button onClick={() => setLocale(nextLocale)} title={dict.language.title} style={btn}><IconGlobe /> {LOCALE_LABEL[nextLocale]}</button>
      </div>
      <button className="zcomfort-more" onClick={onOpenPanel} title={tc.title}
        style={{ display: "none", alignItems: "center", gap: 7, height: 42, padding: "0 14px", border: "1px solid var(--line-strong)", background: "var(--surface)", color: "var(--ink-soft)", borderRadius: "var(--r-pill)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}>
        <IconCalm /> {tc.title}
      </button>
    </>
  );
}

interface ComfortPanelProps {
  dict: ReturnType<typeof getDictionary>;
  locale: Locale;
  calm: boolean;
  bigText: boolean;
  spacing: boolean;
  contrast: boolean;
  dark: boolean;
  toggle: (key: "calm" | "bigText" | "spacing" | "contrast" | "dark") => void;
  setLocale: (l: Locale) => void;
  onClose: () => void;
}

function ComfortPanel({ dict, locale, calm, bigText, spacing, contrast, dark, toggle, setLocale, onClose }: ComfortPanelProps) {
  const tc = dict.comfort;
  const nextLocale: Locale = locale === "en" ? "ms" : "en";
  const btnStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 12, width: "100%", minHeight: 52,
    padding: "0 16px", border: "1px solid var(--line-strong)", background: "var(--surface)",
    color: "var(--ink-soft)", borderRadius: "var(--r-pill)", fontFamily: "var(--font-body)",
    fontWeight: 600, fontSize: 15, cursor: "pointer", textAlign: "left" as const,
  };
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(35,44,56,.32)", zIndex: 70 }} />
      <div style={{ position: "fixed", insetInline: 0, bottom: 0, background: "var(--bg)", borderTopLeftRadius: "var(--r-xl)", borderTopRightRadius: "var(--r-xl)", padding: "24px 20px 32px", zIndex: 71, boxShadow: "var(--sh-2)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 20, color: "var(--ink-deep)" }}>{tc.title}</span>
          <button onClick={onClose} style={{ width: 38, height: 38, border: 0, background: "var(--surface-sunk)", borderRadius: "var(--r-sm)", cursor: "pointer", color: "var(--ink-soft)", display: "grid", placeItems: "center" }}><IconClose /></button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button className="zcomfort" data-on={String(calm)} onClick={() => toggle("calm")} style={btnStyle}><IconCalm /> {tc.calmMode}</button>
          <button className="zcomfort" data-on={String(bigText)} onClick={() => toggle("bigText")} style={btnStyle}><IconText /> {tc.biggerText}</button>
          <button className="zcomfort" data-on={String(spacing)} onClick={() => toggle("spacing")} style={btnStyle}><IconSpacing /> {tc.readingSpacing}</button>
          <button className="zcomfort" data-on={String(contrast)} onClick={() => toggle("contrast")} style={btnStyle}><IconContrast /> {tc.higherContrast}</button>
          <button className="zcomfort" data-on={String(dark)} onClick={() => toggle("dark")} style={btnStyle}><IconMoon /> {tc.darkTheme}</button>
          <button onClick={() => { setLocale(nextLocale); onClose(); }} style={{ ...btnStyle, marginTop: 4 }}><IconGlobe /> {LOCALE_LABEL[nextLocale]}</button>
        </div>
      </div>
    </>
  );
}

// ---- Nav item button -------------------------------------------------------

function NavItem({ label, active, onClick, icon }: { label: string; active: boolean; onClick: () => void; icon: React.ReactNode }) {
  return (
    <button className="znav-item" data-on={String(active)} onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 48, padding: "0 14px", border: 0, background: "transparent", color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 15.5, borderRadius: "var(--r-md)", cursor: "pointer", textAlign: "start", width: "100%" }}>
      {icon} {label}
    </button>
  );
}

// ---- Admin sign-in screen -------------------------------------------------

interface AdminSignInScreenProps {
  dict: ReturnType<typeof getDictionary>;
  locale: Locale;
  email: string;
  sending: boolean;
  onEmail: (v: string) => void;
  onSend: () => void;
  onSimulateNotOrg: () => void;
  setLocale: (l: Locale) => void;
}

function AdminSignInScreen({ dict, locale, email, sending, onEmail, onSend, onSimulateNotOrg, setLocale }: AdminSignInScreenProps) {
  const t = dict.admin.signIn;
  const c = dict.common;
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -160, insetInlineStart: -120, width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, var(--lilac-tint), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -180, insetInlineEnd: -120, width: 560, height: 560, borderRadius: "50%", background: "radial-gradient(circle, var(--brand-tint), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: 24, insetInlineEnd: 28, display: "flex", gap: 3, padding: 4, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-pill)", boxShadow: "var(--sh-1)" }}>
        <button className="zseg" data-on={String(locale === "en")} onClick={() => setLocale("en")} style={{ border: 0, background: "transparent", color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13, padding: "8px 13px", borderRadius: "var(--r-pill)", cursor: "pointer" }}>English</button>
        <button className="zseg" data-on={String(locale === "ms")} onClick={() => setLocale("ms")} style={{ border: 0, background: "transparent", color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13, padding: "8px 13px", borderRadius: "var(--r-pill)", cursor: "pointer" }}>Bahasa Melayu</button>
      </div>
      <div style={{ position: "relative", width: "100%", maxWidth: 440 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28, justifyContent: "center" }}>
          <ZooyloLogo size={40} />
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 28, color: "var(--brand)", letterSpacing: "-.02em" }}>zooylo</span>
        </div>
        <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-xl)", boxShadow: "var(--sh-1)", padding: "36px 34px" }}>
          <span style={{ fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 13, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--ink-faint)" }}>Organisation portal</span>
          <h1 style={{ fontSize: 26, marginTop: 12, color: "var(--ink-deep)", margin: "12px 0 0" }}>{t.title}</h1>
          <p style={{ color: "var(--ink-soft)", marginTop: 10, fontSize: 16, lineHeight: 1.6, margin: "10px 0 0" }}>{t.body}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 24 }}>
            <label htmlFor="ap-email-input" style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)", letterSpacing: ".02em" }}>{t.emailLabel}</label>
            <input id="ap-email-input" value={email} onChange={(e) => onEmail(e.target.value)} type="email" placeholder="you@centre.org"
              style={{ minHeight: 52, padding: "14px 16px", fontSize: 17, fontFamily: "var(--font-body)", color: "var(--ink)", background: "var(--surface)", border: "2px solid var(--line-strong)", borderRadius: "var(--r-md)", width: "100%", outline: "none" }} />
            <span style={{ fontSize: 13.5, color: "var(--ink-faint)" }}>{t.emailHint}</span>
          </div>
          <button onClick={onSend} disabled={sending}
            style={{ marginTop: 22, width: "100%", minHeight: 54, border: 0, borderRadius: "var(--r-pill)", background: "var(--brand)", color: "#fff", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 17, cursor: sending ? "wait" : "pointer", boxShadow: "var(--sh-1)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            {sending && <span style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.5)", borderTopColor: "#fff", borderRadius: "50%", animation: "zspin .8s linear infinite" }} />}
            {sending ? c.sending : c.sendMagicLink}
          </button>
          <div style={{ marginTop: 14, textAlign: "center" }}>
            <button onClick={onSimulateNotOrg} style={{ background: "transparent", border: 0, color: "var(--ink-faint)", fontFamily: "var(--font-body)", fontSize: 13.5, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Simulate not-in-org</button>
          </div>
        </div>
        <p style={{ textAlign: "center", color: "var(--ink-faint)", fontSize: 13.5, marginTop: 18 }}>{t.guardrail}</p>
      </div>
    </div>
  );
}

// ---- Sent + expired + not-org screens ------------------------------------

function AdminSentScreen({ dict, email, onContinue, onDifferentEmail, onSimulateExpired }: { dict: ReturnType<typeof getDictionary>; email: string; onContinue: () => void; onDifferentEmail: () => void; onSimulateExpired: () => void }) {
  const c = dict.common;
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -160, insetInlineEnd: -120, width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, var(--brand-tint), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 440, textAlign: "center" }}>
        <div style={{ width: 88, height: 88, borderRadius: "var(--r-lg)", background: "var(--brand-tint)", display: "grid", placeItems: "center", margin: "0 auto 22px" }}><IconMail /></div>
        <h1 style={{ fontSize: 28, color: "var(--ink-deep)", margin: 0 }}>{c.checkInbox}</h1>
        <p style={{ color: "var(--ink-soft)", marginTop: 12, fontSize: 16, lineHeight: 1.6, margin: "12px 0 0" }}>We sent a link to <b style={{ color: "var(--ink)" }}>{email || "your inbox"}</b>. Tap it to sign in.</p>
        <button onClick={onContinue} style={{ marginTop: 26, minHeight: 54, padding: "0 30px", border: 0, borderRadius: "var(--r-pill)", background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: 17, cursor: "pointer", boxShadow: "var(--sh-1)" }}>{dict.common.continue}</button>
        <div style={{ marginTop: 16, display: "flex", gap: 18, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={onDifferentEmail} style={{ background: "transparent", border: 0, color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontSize: 15, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>{c.useDifferentEmail}</button>
          <button onClick={onSimulateExpired} style={{ background: "transparent", border: 0, color: "var(--ink-faint)", fontFamily: "var(--font-body)", fontSize: 15, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Simulate expired link</button>
        </div>
      </div>
    </div>
  );
}

function AdminExpiredScreen({ dict, onSend }: { dict: ReturnType<typeof getDictionary>; onSend: () => void }) {
  const c = dict.common;
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", bottom: -180, insetInlineStart: -120, width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, var(--peach-tint), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 440, textAlign: "center" }}>
        <div style={{ width: 88, height: 88, borderRadius: "var(--r-lg)", background: "var(--peach-tint)", display: "grid", placeItems: "center", margin: "0 auto 22px" }}><IconWarning /></div>
        <h1 style={{ fontSize: 28, color: "var(--ink-deep)", margin: 0 }}>{c.linkExpired}</h1>
        <p style={{ color: "var(--ink-soft)", marginTop: 12, fontSize: 16, lineHeight: 1.6, margin: "12px 0 0" }}>{dict.service.signIn.expiredBody}</p>
        <button onClick={onSend} style={{ marginTop: 26, minHeight: 54, padding: "0 30px", border: 0, borderRadius: "var(--r-pill)", background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: 17, cursor: "pointer", boxShadow: "var(--sh-1)" }}>{c.sendNewLink}</button>
      </div>
    </div>
  );
}

function AdminNotOrgScreen({ dict, onBack }: { dict: ReturnType<typeof getDictionary>; onBack: () => void }) {
  const t = dict.admin.signIn;
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -160, insetInlineEnd: -120, width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, var(--peach-tint), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 460, textAlign: "center" }}>
        <div style={{ width: 88, height: 88, borderRadius: "var(--r-lg)", background: "var(--peach-tint)", display: "grid", placeItems: "center", margin: "0 auto 22px" }}><IconPersonQuestion /></div>
        <h1 style={{ fontSize: 26, color: "var(--ink-deep)", margin: 0 }}>{t.notMemberTitle}</h1>
        <p style={{ color: "var(--ink-soft)", marginTop: 12, fontSize: 16, lineHeight: 1.65, margin: "12px 0 0" }}>Ask your organisation admin to invite you. Admins can manage seats from the Members screen.</p>
        <button onClick={onBack} style={{ marginTop: 24, minHeight: 52, padding: "0 28px", border: "2px solid var(--line-strong)", background: "transparent", color: "var(--ink)", borderRadius: "var(--r-pill)", fontWeight: 600, fontSize: 16, cursor: "pointer" }}>Back to sign in</button>
      </div>
    </div>
  );
}

// ---- AdminPortal ----------------------------------------------------------

interface AdminPortalProps {
  /** Signed-in account email (real-auth path). Undefined in the demo path. */
  accountEmail?: string;
  /** Signed-in Supabase user id (real-auth path). Passed to screens via Outlet. */
  accountId?: string;
  /**
   * The signed-in admin's real organisation name, resolved by the gate (see
   * app/admin/OrgContext). Absent on the demo path, where the sidebar keeps its
   * neutral placeholder rather than naming a fixture org as if it were theirs.
   */
  orgName?: string;
  /** Real sign-out. When absent, the built-in demo sign-in stub is shown instead. */
  onSignOut?: () => void;
}

export function AdminPortal({ accountEmail, accountId, orgName, onSignOut }: AdminPortalProps = {}) {
  const { calm, bigText, spacing, contrast, dark, locale, toggle, setLocale } = useComfort();
  const dict = getDictionary(locale);
  const nav = dict.admin.nav;
  const [authView, setAuthView] = useState<AdminAuthView>("app");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [comfortOpen, setComfortOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Real sign-out when the gate provides it; otherwise the demo sign-in stub.
  const doSignOut = onSignOut ?? (() => setAuthView("signin"));

  // Nav paths depend on the host: on admin.zooylo.app the admin screens ARE the
  // root ("/members"), everywhere else they sit under "/admin/members".
  const mode = portalMode();
  const path = (sub: string) => adminPath(sub, mode);
  const here = location.pathname.replace(/\/+$/, "") || "/";
  const isAt = (sub: string) => here === (path(sub).replace(/\/+$/, "") || "/");

  const active = {
    overview: isAt(""),
    kids: isAt("kids"),
    licences: isAt("licences"),
    billing: isAt("billing"),
    members: isAt("members"),
    consent: isAt("consent"),
    data: isAt("data"),
    settings: isAt("settings"),
  };
  const n2 = dict.admin.nav2;
  const profile = useOrgProfile();
  const isPlatformAdmin = usePlatformAdmin();
  const demoVariant = useDemoVariant();
  const showDemoSwitch = !shouldUseSupabase();

  function handleSendLink() {
    if (sending) return;
    setSending(true);
    setTimeout(() => { setSending(false); setAuthView("sent"); }, 800);
  }

  function goTo(to: string) {
    navigate(to);
    setNavOpen(false);
  }

  if (authView === "signin") {
    return (
      <div className="zp-root" style={{ fontFamily: "var(--font-body)", color: "var(--ink)", background: "var(--bg)", minHeight: "100vh" }}>
        <AdminSignInScreen dict={dict} locale={locale} email={email} sending={sending} onEmail={setEmail} onSend={handleSendLink} onSimulateNotOrg={() => setAuthView("notOrg")} setLocale={setLocale} />
      </div>
    );
  }
  if (authView === "sent") {
    return (
      <div className="zp-root" style={{ fontFamily: "var(--font-body)", color: "var(--ink)", background: "var(--bg)", minHeight: "100vh" }}>
        <AdminSentScreen dict={dict} email={email} onContinue={() => setAuthView("app")} onDifferentEmail={() => setAuthView("signin")} onSimulateExpired={() => setAuthView("expired")} />
      </div>
    );
  }
  if (authView === "expired") {
    return (
      <div className="zp-root" style={{ fontFamily: "var(--font-body)", color: "var(--ink)", background: "var(--bg)", minHeight: "100vh" }}>
        <AdminExpiredScreen dict={dict} onSend={handleSendLink} />
      </div>
    );
  }
  if (authView === "notOrg") {
    return (
      <div className="zp-root" style={{ fontFamily: "var(--font-body)", color: "var(--ink)", background: "var(--bg)", minHeight: "100vh" }}>
        <AdminNotOrgScreen dict={dict} onBack={() => setAuthView("signin")} />
      </div>
    );
  }

  // App shell
  return (
    <div
      className="zp-root"
      data-nav-open={String(navOpen)}
      style={{ fontFamily: "var(--font-body)", color: "var(--ink)", background: "var(--bg)", minHeight: "100vh", fontSize: 16, display: "flex" }}
    >
      <div className="znav-scrim" onClick={() => setNavOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(35,44,56,.32)", zIndex: 55 }} />

      <aside className="znav-rail" style={{ flex: "none", width: 262, background: "var(--surface)", borderInlineEnd: "1px solid var(--line)", display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh" }}>
        <div style={{ padding: "22px 20px 8px", display: "flex", alignItems: "center", gap: 11, justifyContent: "space-between" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <ZooyloLogo size={34} />
            <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 23, color: "var(--brand)", letterSpacing: "-.02em" }}>zooylo</span>
          </span>
          <button className="zhamb" onClick={() => setNavOpen(false)} title="Close menu"
            style={{ width: 38, height: 38, border: 0, background: "var(--surface-sunk)", borderRadius: "var(--r-sm)", cursor: "pointer", color: "var(--ink-soft)", display: "grid", placeItems: "center" }}>
            <IconClose />
          </button>
        </div>
        <div style={{ margin: "4px 16px 8px", padding: "10px 12px", background: "var(--surface-warm)", borderRadius: "var(--r-md)" }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--ink-faint)" }}>Organisation</span>
          <b style={{ display: "block", fontSize: 14.5, color: "var(--ink-deep)", fontFamily: "var(--font-body)", marginTop: 3, lineHeight: 1.2, wordBreak: "break-word" }}>{orgName || "Your organisation"}</b>
        </div>

        <nav aria-label={dict.admin.nav.manage} style={{ flex: 1, padding: "8px 16px", display: "flex", flexDirection: "column", gap: 4, overflowY: "auto", overflowX: "hidden" }}>
          {/* `path(...)` is adminPath(sub, mode) — NEVER a hardcoded "/admin/…".
              On admin.zooylo.app the admin screens ARE the root. */}
          <NavItem label={nav.overview} active={active.overview} onClick={() => goTo(path(""))} icon={<IconOverview />} />
          <NavItem label={n2.children} active={active.kids} onClick={() => goTo(path("kids"))} icon={<IconRoster />} />
          <NavItem label={n2.licences} active={active.licences} onClick={() => goTo(path("licences"))} icon={<IconSponsored />} />
          <NavItem label={n2.billing} active={active.billing} onClick={() => goTo(path("billing"))} icon={<IconSeats />} />
          <NavItem label={n2.users} active={active.members} onClick={() => goTo(path("members"))} icon={<IconMembers />} />
          <NavItem label={n2.settings} active={active.settings} onClick={() => goTo(path("settings"))} icon={<IconSettings />} />
          <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--ink-faint)", padding: "14px 12px 4px" }}>{n2.consentData}</span>
          <NavItem label={n2.consent} active={active.consent} onClick={() => goTo(path("consent"))} icon={<IconConsentShield />} />
          <NavItem label={n2.data} active={active.data} onClick={() => goTo(path("data"))} icon={<IconData />} />
          {isPlatformAdmin && (
            <>
              <span aria-hidden="true" style={{ height: 1, background: "var(--line)", margin: "10px 6px" }} />
              <NavItem label={n2.console} active={false} onClick={() => goTo("/console")} icon={<IconOverview />} />
            </>
          )}
        </nav>

        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--line)" }}>
          {showDemoSwitch && (
            <label style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 6px 10px", fontSize: 12, fontWeight: 600, color: "var(--ink-faint)" }}>
              {n2.demoAs}
              <select
                value={demoVariant}
                onChange={(e) => setDemoVariant(e.target.value as DemoVariant)}
                style={{ minHeight: 40, borderRadius: "var(--r-md)", border: "1px solid var(--line-strong)", background: "var(--surface)", color: "var(--ink)", fontFamily: "var(--font-body)", fontSize: 14, padding: "0 8px" }}
              >
                <option value="centre">{n2.demoCentre}</option>
                <option value="individual">{n2.demoIndividual}</option>
                <option value="suspended">{n2.demoSuspended}</option>
              </select>
            </label>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "8px 10px", background: "var(--surface-warm)", borderRadius: "var(--r-md)", marginTop: 4 }}>
            <span style={{ width: 38, height: 38, borderRadius: "50%", background: "var(--lilac-tint)", color: "var(--lilac-deep)", display: "grid", placeItems: "center", flex: "none", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}>A</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <b style={{ display: "block", fontSize: 14, color: "var(--ink-deep)", fontFamily: "var(--font-body)", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{accountEmail ?? "Admin"}</b>
              <span style={{ fontSize: 11.5, color: "var(--lilac-deep)", fontWeight: 600 }}>Admin</span>
            </div>
            <button onClick={doSignOut} title={dict.common.signOut}
              style={{ flex: "none", width: 34, height: 34, border: 0, background: "transparent", color: "var(--ink-faint)", borderRadius: "var(--r-sm)", cursor: "pointer", display: "grid", placeItems: "center" }}>
              <IconSignOut />
            </button>
          </div>
        </div>
      </aside>

      <div className="zp-main" style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <header style={{ position: "sticky", top: 0, zIndex: 20, background: "var(--bg)", borderBottom: "1px solid var(--line)", padding: "0 28px", minHeight: 70, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
            <button className="zhamb" onClick={() => setNavOpen(true)} title="Menu"
              style={{ flex: "none", width: 42, height: 42, border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-md)", cursor: "pointer", display: "grid", placeItems: "center", color: "var(--ink-soft)" }}>
              <IconMenu />
            </button>
            <span className="ztopbar-eyebrow" style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--ink-faint)", whiteSpace: "nowrap" }}>Admin portal</span>
          </div>
          <ComfortCluster dict={dict} locale={locale} calm={calm} bigText={bigText} spacing={spacing} contrast={contrast} dark={dark} toggle={toggle} setLocale={setLocale} onOpenPanel={() => setComfortOpen(true)} />
        </header>

        <main style={{ flex: 1, overflowY: "auto" }}>
          <div style={{ maxWidth: 1080, margin: "0 auto", padding: "30px 28px 60px" }}>
            <OrgStatusBanner status={profile.status} />
            <Outlet context={{ signOut: doSignOut, userId: accountId, email: accountEmail, orgName }} />
          </div>
        </main>
      </div>

      {comfortOpen && (
        <ComfortPanel dict={dict} locale={locale} calm={calm} bigText={bigText} spacing={spacing} contrast={contrast} dark={dark} toggle={toggle} setLocale={setLocale} onClose={() => setComfortOpen(false)} />
      )}
    </div>
  );
}
